/**
 * GEO-Enhanced Schema Component
 * Adds additional structured data for AI visibility (AEO/GEO)
 * This is ADDITIVE to the existing StructuredData.tsx — does not replace it.
 * 
 * Includes:
 * - Enhanced Organization with sameAs, knowsAbout, and entity identifiers
 * - Detailed Physician schemas with credentials and sameAs
 * - HealthcareService schemas for each department
 * - Speakable schema for voice assistant extraction
 * - Event schemas for limited-time offers/promotions
 * - ContactPoint schema with multiple channels (phone, WhatsApp, email)
 * - VideoObject schema placeholder for future video content
 */

const BASE_URL = "https://bellaroseclinics.ae";

// Enhanced entity schema with full knowledge graph signals
const entitySchema = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "@id": `${BASE_URL}/#medicalclinic`,
  name: "Bella Rose Medical Center",
  alternateName: ["مركز بيلا روز الطبي", "Bella Rose MC", "Bella Rose Clinic Dubai"],
  url: BASE_URL,
  description: "DHA-licensed multi-specialty medical center in Al Mamzar, Dubai offering dental care, dermatology, weight loss, hair transplant, laser treatments, cosmetic surgery, and Endolift. Open daily 10AM-11PM with free consultations and installment payment plans via Tabby and Tamara.",
  slogan: "With You Until The Best Of You",
  foundingDate: "2018",
  foundingLocation: {
    "@type": "Place",
    name: "Al Mamzar, Dubai, UAE",
  },
  areaServed: [
    { "@type": "City", name: "Dubai", "@id": "https://www.wikidata.org/wiki/Q612" },
    { "@type": "Country", name: "United Arab Emirates", "@id": "https://www.wikidata.org/wiki/Q878" },
  ],
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: 25.2949675, longitude: 55.3511937 },
    geoRadius: "30000",
  },
  knowsAbout: [
    "Hollywood Smile Veneers",
    "Dental Implants German and French",
    "Clear Aligners and Metal Braces",
    "Botox and Dermal Fillers",
    "DHI Hair Transplant",
    "FUE Hair Transplant",
    "Fat Dissolving Injections",
    "CoolSculpting Fat Freezing",
    "Endolift Non-Surgical Facelift",
    "Laser Hair Removal",
    "Weight Loss Treatments",
    "Rhinoplasty",
    "Liposuction",
    "IV Drip Therapy",
    "Skin Boosters and Exosome Therapy",
    "Sculptra Collagen Stimulation",
    "Onda Body Contouring",
    "Appetite Suppressant Injections",
    "Pediatric Care",
    "General Surgery",
  ],
  sameAs: [
    "https://www.instagram.com/bellarosemc.ae/",
    "https://www.facebook.com/bellarosemc.ae",
    "https://www.tiktok.com/@bellarose_medical_center",
    "https://www.snapchat.com/add/bellarosemc.ae0",
    "https://www.linkedin.com/company/bellarosemc",
    "https://www.fresha.com/lvp/bella-rose-medical-center-cairo-street-dubai-QvbMZR",
    "https://www.doctify.com/en-ae/practice/bella-rose-medical-center",
    "https://dubai.clinic/clinics/bella-rose-medical-center-in-al-mamzar-dubai-0047774",
    "https://maps.app.goo.gl/WLR3tdjmDChx5coh8",
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Medical License",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "Dubai Health Authority",
        alternateName: "DHA",
        url: "https://www.dha.gov.ae",
      },
    },
  ],
  memberOf: [
    {
      "@type": "GovernmentOrganization",
      name: "Dubai Health Authority",
      url: "https://www.dha.gov.ae",
    },
  ],
  keywords: "dental clinic Dubai, dermatology Dubai, Hollywood smile Dubai, hair transplant Dubai, weight loss clinic Dubai, Botox Dubai, fillers Dubai, dental implants Dubai, clear aligners Dubai, fat dissolving Dubai, Endolift Dubai, cosmetic surgery Dubai, Al Mamzar clinic",
  isAccessibleForFree: false,
  makesOffer: [
    {
      "@type": "Offer",
      name: "Free Initial Consultation",
      description: "Complimentary first consultation with specialist doctors for all services",
      price: "0",
      priceCurrency: "AED",
    },
    {
      "@type": "Offer",
      name: "Installment Payment Plans",
      description: "Split payments into 4 interest-free installments via Tabby or Tamara. Available for treatments up to 100,000 AED.",
      availableAtOrFrom: { "@id": `${BASE_URL}/#medicalclinic` },
    },
  ],
};

// Speakable schema for voice assistant extraction
const speakableSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${BASE_URL}/#webpage`,
  name: "Bella Rose Medical Center — Dental, Dermatology & Weight Loss in Dubai",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", ".hero-description", ".about-section"],
  },
  mainEntity: { "@id": `${BASE_URL}/#medicalclinic` },
};

// Event schemas for limited-time offers/promotions
const getEndOfMonth = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return end.toISOString();
};

const eventSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "Hollywood Smile Package — Limited Time Offer",
    description: "Get your dream smile with premium 3D veneers. Includes free consultation and digital smile design at Bella Rose Medical Center Dubai.",
    startDate: new Date().toISOString().split("T")[0],
    endDate: getEndOfMonth(),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Bella Rose Medical Center",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Muhammad Bin Rashid Charity Association Building, Cairo Street, Al Mamzar",
        addressLocality: "Dubai",
        addressRegion: "Dubai",
        addressCountry: "AE",
      },
    },
    organizer: { "@id": `${BASE_URL}/#medicalclinic` },
    offers: {
      "@type": "Offer",
      url: `${BASE_URL}/offers`,
      availability: "https://schema.org/InStock",
      validFrom: new Date().toISOString().split("T")[0],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "Full Body Laser Package — 6 Sessions",
    description: "Say goodbye to unwanted hair with latest generation laser technology. Safe for all skin types. Package of 6 sessions at Bella Rose Medical Center Dubai.",
    startDate: new Date().toISOString().split("T")[0],
    endDate: getEndOfMonth(),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Bella Rose Medical Center",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Muhammad Bin Rashid Charity Association Building, Cairo Street, Al Mamzar",
        addressLocality: "Dubai",
        addressRegion: "Dubai",
        addressCountry: "AE",
      },
    },
    organizer: { "@id": `${BASE_URL}/#medicalclinic` },
    offers: {
      "@type": "Offer",
      url: `${BASE_URL}/offers`,
      availability: "https://schema.org/InStock",
      validFrom: new Date().toISOString().split("T")[0],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "Botox & Filler Combo — Limited Time",
    description: "Refresh your look with premium European Botox and filler. Includes free consultation and follow-up session at Bella Rose Medical Center Dubai.",
    startDate: new Date().toISOString().split("T")[0],
    endDate: getEndOfMonth(),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Bella Rose Medical Center",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Muhammad Bin Rashid Charity Association Building, Cairo Street, Al Mamzar",
        addressLocality: "Dubai",
        addressRegion: "Dubai",
        addressCountry: "AE",
      },
    },
    organizer: { "@id": `${BASE_URL}/#medicalclinic` },
    offers: {
      "@type": "Offer",
      url: `${BASE_URL}/offers`,
      availability: "https://schema.org/InStock",
      validFrom: new Date().toISOString().split("T")[0],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "Weight Loss Program — Comprehensive Package",
    description: "Comprehensive weight loss program with doctor supervision. Includes body composition analysis, diet plan, and follow-up visits at Bella Rose Medical Center Dubai.",
    startDate: new Date().toISOString().split("T")[0],
    endDate: getEndOfMonth(),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Bella Rose Medical Center",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Muhammad Bin Rashid Charity Association Building, Cairo Street, Al Mamzar",
        addressLocality: "Dubai",
        addressRegion: "Dubai",
        addressCountry: "AE",
      },
    },
    organizer: { "@id": `${BASE_URL}/#medicalclinic` },
    offers: {
      "@type": "Offer",
      url: `${BASE_URL}/offers`,
      availability: "https://schema.org/InStock",
      validFrom: new Date().toISOString().split("T")[0],
    },
  },
];

// ContactPoint schema with multiple channels (phone, WhatsApp, email)
const contactPointSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "@id": `${BASE_URL}/#contactpoints`,
  name: "Bella Rose Medical Center",
  url: BASE_URL,
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+971-4-296-4442",
      contactType: "customer service",
      areaServed: "AE",
      availableLanguage: ["English", "Arabic"],
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "10:00",
        closes: "23:00",
      },
    },
    {
      "@type": "ContactPoint",
      telephone: "+971-52-517-1078",
      contactType: "reservations",
      contactOption: "TollFree",
      areaServed: "AE",
      availableLanguage: ["English", "Arabic"],
      description: "WhatsApp booking line — instant appointment scheduling",
    },
    {
      "@type": "ContactPoint",
      email: "info@bellaroseclinics.ae",
      contactType: "customer service",
      areaServed: "AE",
      availableLanguage: ["English", "Arabic"],
    },
  ],
};

// VideoObject placeholder for future video content
const videoObjectSchema = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: "Bella Rose Medical Center — Clinic Tour",
  description: "Take a virtual tour of Bella Rose Medical Center in Al Mamzar, Dubai. See our state-of-the-art facilities, treatment rooms, and meet our specialist doctors.",
  thumbnailUrl: "https://bellaroseclinics.ae/logo.png",
  uploadDate: "2024-01-01",
  contentUrl: `${BASE_URL}/#video`,
  embedUrl: `${BASE_URL}/#video`,
  publisher: {
    "@type": "Organization",
    name: "Bella Rose Medical Center",
    url: BASE_URL,
  },
};

export function GeoSchema() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(entitySchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableSchema) }}
      />
      {/* Event schemas for limited-time offers */}
      {eventSchemas.map((schema, i) => (
        <script
          key={`geo-event-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      {/* ContactPoint schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactPointSchema) }}
      />
      {/* VideoObject placeholder */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoObjectSchema) }}
      />
    </>
  );
}
